/**
 * Session timer plus the exit affordance, rendered inline in the plan header
 * next to the Calendar/Metric toggle.
 *
 * The pill is the extend control at all times; inside the warning window it
 * only recolours and sharpens its wording, so there is no extra surface and no
 * layout shift.
 */
import { type FC, useCallback } from 'react';
import { Button, Tooltip } from 'antd';

import LogOut02 from 'assets/svg/LogOut02';

import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import { TPO_EDIT_SESSION_EXIT_BTN, TPO_EDIT_SESSION_TIMER } from '../../../config/testIds';
import { useEditSession } from '../EditSessionContext';
import { EMPTY_SESSION_TIME, formatSessionTime } from '../utils/formatSessionTime';

const BASE_PILL_CLASS = 'rounded-lg px-2 py-0.5 text-xs leading-6 transition-colors';
const ACTIVE_PILL_CLASS = 'bg-[#F6FFED] text-[#52C41A] cursor-pointer hover:bg-[#EAFBDD]';
const WARNING_PILL_CLASS = 'bg-[#FFF3E0] text-[#B26A00] cursor-pointer hover:bg-[#FFE0B2]';
// AntD's stylesheet is imported after Tailwind's utilities, so `.ant-btn`'s
// `display: inline-block` wins over a plain `inline-flex` class and the icon
// wraps above the label. The important prefix is how the rest of the app
// overrides AntD; see PPAConfigValidation for the same pattern.
const EXIT_BTN_CLASS = '!inline-flex !items-center gap-x-1 !px-0 text-xs font-semibold !text-[#44338A]';

const EditSessionControls: FC = () => {
    const tpoGenConfig = useTpoGenConfig();
    const { canEdit, isLockEnabled, secondsRemaining, isExpiringSoon, isReleasing, release, openExtendPrompt } =
        useEditSession();

    const handleExit = useCallback(() => {
        void release();
    }, [release]);

    if (!isLockEnabled || !canEdit) return null;

    const labels = tpoGenConfig.labels.editSession;
    const timer = (
        <span
            className={`${BASE_PILL_CLASS} ${isExpiringSoon ? WARNING_PILL_CLASS : ACTIVE_PILL_CLASS}`}
            data-testid={TPO_EDIT_SESSION_TIMER}
            data-state-expiring={isExpiringSoon ? 'true' : undefined}
            onClick={openExtendPrompt}
        >
            {`${labels.sessionTimePrefix}${
                secondsRemaining === null ? EMPTY_SESSION_TIME : formatSessionTime(secondsRemaining)
            }`}
        </span>
    );

    return (
        <div className="flex flex-row items-center gap-x-5">
            <Tooltip title={isExpiringSoon ? labels.extendTooltip : labels.clickToExtendTooltip}>{timer}</Tooltip>
            <Button
                type="text"
                size="small"
                className={EXIT_BTN_CLASS}
                icon={<LogOut02 />}
                loading={isReleasing}
                onClick={handleExit}
                data-testid={TPO_EDIT_SESSION_EXIT_BTN}
            >
                {labels.exitEditMode}
            </Button>
        </div>
    );
};

EditSessionControls.displayName = 'EditSessionControls';
export default EditSessionControls;
